import {
  GearSix,
  GitMerge,
  Paperclip,
  TrashSimple,
} from '@phosphor-icons/react'
import { Button, Popover, Tooltip } from 'antd'
import { useContext, useState } from 'react'
import { useParams } from 'react-router-dom'

import MergeClients from '@/components/MergeClients/MergeClients'
import usePermissions from '@/hooks/usePermissions'
import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import useSearchParams from '@/hooks/useSearchParams'
import {
  invalidLoadCustomerById,
  useLoadCustomer,
} from '@/queries/customers/useLoadCustomer'
import { ZentakeContext } from '@/utils/ZentakeContext'

import CustomerFileAttachment from '../CustomerFileAttachment'
import { ConfirmDeleteClientModal } from './ConfirmDeleteClientModal'

type CustomerFileAttachmentModalInfoProps = {
  open: boolean
  id?: string
  value?: string
}

export const ClientHeaderSettings = () => {
  const { clientId } = useParams<{ clientId: string }>()
  const searchParams = useSearchParams()
  const updateParams = useUpdateURLSearchParams()
  const { data: clientDetail } = useLoadCustomer(clientId, {
    enabled: !!clientId,
  })
  const { teamDetails } = useContext(ZentakeContext)

  const [isOpenPopover, setIsOpenPopover] = useState(false)
  const [
    isCustomerFileAttachmentModalInfo,
    setIsCustomerFileAttachmentModalInfo,
  ] = useState<CustomerFileAttachmentModalInfoProps | undefined>(undefined)
  const { canMergeClientProfile, canDeleteClientProfile } = usePermissions()

  const onMergePatient = searchParams.get('mergePatient') === 'open'
  const deletePatient = searchParams.get('deletePatient') === 'open'

  const settingList = [
    ...(canDeleteClientProfile
      ? [
          {
            key: 'delete',
            content: (
              <div className="flex items-center justify-center gap-1 text-red-700">
                <TrashSimple className="text-base" /> Delete
              </div>
            ),
            onClick: () => updateParams(['deletePatient', 'open']),
          },
        ]
      : []),
    ...(canMergeClientProfile
      ? [
          {
            key: 'merge',
            content: (
              <div className="flex items-center justify-center gap-1">
                <GitMerge className="text-base" /> Merge
              </div>
            ),
            onClick: () => updateParams(['mergePatient', 'open']),
          },
        ]
      : []),
    ...(teamDetails?.customer_file_attachments
      ? [
          {
            key: 'customer-file-attachment',
            content: (
              <div className="flex items-center justify-center gap-1">
                <Paperclip className="text-base" /> Add File Attachment
              </div>
            ),
            onClick: () => setIsCustomerFileAttachmentModalInfo({ open: true }),
          },
        ]
      : []),
  ]

  return (
    <>
      <Popover
        classNames={{ body: 'p-2' }}
        content={
          <div className="flex flex-col">
            {settingList.map(({ key, content, onClick }) => (
              <button
                className="flex items-center gap-1 rounded-md py-1 pl-2 pr-3 font-medium !outline-none hover:bg-gray-100/70"
                key={key}
                onClick={() => {
                  onClick()
                  setIsOpenPopover(false)
                }}
              >
                {content}
              </button>
            ))}
          </div>
        }
        onOpenChange={(value) => setIsOpenPopover(value)}
        open={isOpenPopover}
        placement="bottomRight"
        trigger="click"
      >
        <Tooltip mouseEnterDelay={0.3} title="Settings">
          <Button className="h-9 px-2 !outline-none" type="link">
            <GearSix className="text-xl" />
          </Button>
        </Tooltip>
      </Popover>
      {onMergePatient ? (
        <MergeClients
          baseClientMaster={clientDetail}
          handleOut={() => updateParams(['mergePatient', undefined])}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          onMergeCallback={() => invalidLoadCustomerById(clientId)}
        />
      ) : null}
      {isCustomerFileAttachmentModalInfo?.open ? (
        <CustomerFileAttachment
          {...{
            isOpen: Boolean(isCustomerFileAttachmentModalInfo?.open),
            onClose: () => setIsCustomerFileAttachmentModalInfo(undefined),
            clientId: Number(clientId),
          }}
        />
      ) : null}
      {deletePatient ? <ConfirmDeleteClientModal /> : null}
    </>
  )
}
